import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Drawer,
  Form,
  Input,
  Modal,
  Select,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../store/useAuthStore';
import { useOperatorStore } from '../../store/useOperatorStore';
import { useIsEditAllowedWithOperator } from '../../utils/utils';
import { getErrorMessage } from '../../utils/errorHandler';
import useDebounce from '../../utils/debounce';
import WarningMessage from '../../components/WarningMessage';
import {
  useCreateFactory,
  useDeleteFactory,
  useFetchFactories,
  useFetchFactorySelect,
  useUpdateFactory,
} from './Hooks';

const { Title, Text } = Typography;
const TREE_FETCH_LIMIT = 500;

function collectExpandableKeys(nodes = []) {
  const keys = [];
  nodes.forEach((node) => {
    if (node.children?.length) {
      keys.push(node._id);
      keys.push(...collectExpandableKeys(node.children));
    }
  });
  return keys;
}

/** Nest CHILD factories under their PARENT so the table can render as a tree. */
function buildFactoryTree(factories = []) {
  const nodes = new Map();
  factories.forEach((factory) => {
    nodes.set(String(factory._id), { ...factory, key: factory._id, children: [] });
  });

  const roots = [];
  nodes.forEach((node) => {
    const parentId = node.parentFactoryId ? String(node.parentFactoryId) : null;
    if (node.kind === 'CHILD' && parentId && nodes.has(parentId)) {
      nodes.get(parentId).children.push(node);
      return;
    }
    roots.push(node);
  });

  const sortByName = (a, b) =>
    String(a.name || a.number || '').localeCompare(String(b.name || b.number || ''), undefined, {
      sensitivity: 'base',
    });

  nodes.forEach((node) => {
    if (!node.children.length) {
      delete node.children;
      return;
    }
    node.children.sort(sortByName);
  });

  return roots.sort(sortByName);
}

export default function FactoriesPage() {
  const { t } = useTranslation('factory');
  const { user } = useAuthStore();
  const { operatorId } = useOperatorStore();
  const isEditAllowed = useIsEditAllowedWithOperator();
  const isReadOnly = user?.role === 'READ_ONLY_ADMIN';
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebounce(searchQuery, 400);
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [kind, setKind] = useState('PARENT');
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [form] = Form.useForm();
  const { data: parents = [] } = useFetchFactorySelect({ operatorId, kind: 'PARENT' });

  // Load a wide page so parent + children stay together for nesting.
  const { data, isLoading, isFetching, refetch } = useFetchFactories({
    operatorId,
    page: 1,
    search: debouncedSearch,
    limit: TREE_FETCH_LIMIT,
  });

  const { mutate: createFactory, isPending: creating } = useCreateFactory({
    onSuccess: () => {
      message.success(t('createSuccess'));
      closeDrawer();
    },
    onError: (error) => message.error(getErrorMessage(error, t('actionFailed'))),
  });

  const { mutate: updateFactory, isPending: updating } = useUpdateFactory({
    onSuccess: () => {
      message.success(t('updateSuccess'));
      closeDrawer();
    },
    onError: (error) => message.error(getErrorMessage(error, t('actionFailed'))),
  });

  const { mutate: deleteFactory, isPending: deleting } = useDeleteFactory({
    onSuccess: () => {
      message.success(t('deleteSuccess'));
      setDeleteTarget(null);
    },
    onError: (error) => message.error(getErrorMessage(error, t('actionFailed'))),
  });

  const factories = data?.paginatedData || [];

  const treeData = useMemo(() => buildFactoryTree(factories), [factories]);

  const parentLookup = useMemo(() => {
    const map = new Map();
    parents.forEach((item) => map.set(String(item._id), item));
    factories.forEach((item) => {
      if (item.kind !== 'CHILD') map.set(String(item._id), item);
    });
    return map;
  }, [parents, factories]);

  const pagedTreeData = useMemo(() => {
    const start = (page - 1) * pageSize;
    return treeData.slice(start, start + pageSize);
  }, [treeData, page]);

  useEffect(() => {
    setExpandedRowKeys(collectExpandableKeys(pagedTreeData));
  }, [pagedTreeData]);

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditing(null);
    form.resetFields();
  };

  const openCreate = (preset = {}) => {
    setEditing(null);
    const nextKind = preset.kind || 'PARENT';
    setKind(nextKind);
    form.resetFields();
    form.setFieldsValue({
      kind: nextKind,
      parentFactoryId: preset.parentFactoryId,
    });
    setDrawerOpen(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    form.setFieldsValue({
      number: record.number,
      name: record.name || '',
      kind: record.kind === 'CHILD' ? 'CHILD' : 'PARENT',
      parentFactoryId: record.parentFactoryId || undefined,
    });
    setKind(record.kind === 'CHILD' ? 'CHILD' : 'PARENT');
    setDrawerOpen(true);
  };

  const onFinish = (values) => {
    const selectedParent = parents.find((item) => String(item._id) === String(values.parentFactoryId));
    const payload = {
      operatorId,
      kind: values.kind || 'PARENT',
      number:
        values.kind === 'CHILD'
          ? selectedParent?.number || values.number?.trim()
          : values.number?.trim(),
      name: values.name?.trim() || undefined,
      parentFactoryId: values.kind === 'CHILD' ? values.parentFactoryId : undefined,
    };
    if (editing) {
      updateFactory({ id: editing._id, ...payload });
      return;
    }
    createFactory(payload);
  };

  const columns = [
    {
      title: t('number'),
      dataIndex: 'number',
      key: 'number',
      width: 180,
      render: (value, record) => {
        if (record.kind === 'CHILD') {
          return (
            <Text type="secondary" className="text-xs">
              {value}
            </Text>
          );
        }
        return <Text strong>{value}</Text>;
      },
    },
    {
      title: t('name'),
      dataIndex: 'name',
      key: 'name',
      render: (value, record) => {
        const displayName = value || '-';
        if (record.kind === 'CHILD') {
          const parent = parentLookup.get(String(record.parentFactoryId));
          const parentLabel = parent?.name || parent?.number || record.parentName;
          return (
            <div className="leading-tight">
              <div>{displayName}</div>
              {parentLabel ? (
                <Text type="secondary" className="text-xs">
                  {t('underParent', { parent: parentLabel })}
                </Text>
              ) : null}
            </div>
          );
        }
        const childCount = record.children?.length || 0;
        return (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium">{displayName}</span>
            {childCount > 0 ? (
              <Tag color="default">{t('childCount', { count: childCount })}</Tag>
            ) : null}
          </div>
        );
      },
    },
    {
      title: t('kind'),
      dataIndex: 'kind',
      key: 'kind',
      width: 150,
      align: 'center',
      render: (value) =>
        value === 'CHILD' ? (
          <Tag color="purple">{t('child')}</Tag>
        ) : (
          <Tag color="blue">{t('parent')}</Tag>
        ),
    },
    {
      title: t('actions'),
      key: 'actions',
      width: 160,
      align: 'center',
      render: (_, record) =>
        isEditAllowed && !isReadOnly ? (
          <div className="flex justify-center gap-1">
            {record.kind !== 'CHILD' ? (
              <Tooltip title={t('addChild')}>
                <Button
                  type="text"
                  icon={<PlusOutlined />}
                  onClick={() =>
                    openCreate({ kind: 'CHILD', parentFactoryId: record._id })
                  }
                />
              </Tooltip>
            ) : null}
            <Tooltip title={t('edit')}>
              <Button type="text" icon={<EditOutlined />} onClick={() => openEdit(record)} />
            </Tooltip>
            <Tooltip title={t('delete')}>
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={() => setDeleteTarget(record)}
              />
            </Tooltip>
          </div>
        ) : (
          '-'
        ),
    },
  ];

  return (
    <div className="space-y-4">
      {!operatorId ? (
        <WarningMessage />
      ) : (
        <>
          <div className="flex justify-between items-center gap-4">
            <div className="flex items-center gap-2">
              <Input
                allowClear
                placeholder={t('searchPlaceholder')}
                prefix={<SearchOutlined />}
                style={{ width: 300 }}
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  if (page !== 1) setPage(1);
                }}
              />
              <Tooltip title={t('refresh')}>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={() => refetch()}
                  loading={isFetching}
                />
              </Tooltip>
            </div>
            {isEditAllowed && !isReadOnly && (
              <Button type="primary" icon={<PlusOutlined />} onClick={() => openCreate()}>
                {t('add')}
              </Button>
            )}
          </div>
          <Table
            columns={columns}
            dataSource={pagedTreeData}
            loading={isLoading || isFetching}
            rowKey={(record) => record._id}
            size="small"
            indentSize={28}
            expandable={{
              expandedRowKeys,
              onExpandedRowsChange: setExpandedRowKeys,
            }}
            rowClassName={(record) =>
              record.kind === 'CHILD' ? 'factory-child-row bg-slate-50/80' : 'factory-parent-row'
            }
            pagination={{
              current: page,
              pageSize,
              total: treeData.length,
              showSizeChanger: false,
              onChange: setPage,
              position: ['bottomCenter'],
            }}
          />
        </>
      )}

      <Drawer
        title={
          <Title level={4} className="!mb-0">
            {editing ? t('edit') : t('createTitle')}
          </Title>
        }
        open={drawerOpen}
        onClose={closeDrawer}
        width={420}
        maskClosable={false}
      >
        <Form form={form} layout="vertical" onFinish={onFinish} initialValues={{ kind: 'PARENT' }}>
          <Form.Item name="kind" label={t('kind')}>
            <Select
              disabled={!!editing}
              options={[
                { value: 'PARENT', label: t('parent') },
                { value: 'CHILD', label: t('child') },
              ]}
              onChange={(value) => setKind(value)}
            />
          </Form.Item>
          {kind === 'CHILD' ? (
            <Form.Item
              name="parentFactoryId"
              label={t('parentFactory')}
              rules={[{ required: true, message: t('parentRequired') }]}
            >
              <Select
                showSearch
                optionFilterProp="label"
                options={parents.map((item) => ({
                  value: item._id,
                  label: item.name ? `${item.number} — ${item.name}` : item.number,
                }))}
              />
            </Form.Item>
          ) : (
            <Form.Item
              name="number"
              label={t('number')}
              rules={[
                { required: true, message: t('numberRequired') },
                { pattern: /^\S+$/, message: t('noSpaces') },
                { max: 50 },
              ]}
            >
              <Input placeholder={t('enterNumber')} />
            </Form.Item>
          )}
          <Form.Item
            name="name"
            label={t('name')}
            rules={[
              { required: kind === 'CHILD', whitespace: true, message: t('nameRequiredForChild') },
              { max: 80 },
            ]}
          >
            <Input placeholder={t('enterName')} />
          </Form.Item>
          <div className="flex justify-end gap-2">
            <Button onClick={closeDrawer}>{t('cancel')}</Button>
            <Button type="primary" htmlType="submit" loading={creating || updating}>
              {t('save')}
            </Button>
          </div>
        </Form>
      </Drawer>

      <Modal
        title={<Title level={4} className="!mb-0">{t('deleteTitle')}</Title>}
        open={!!deleteTarget}
        onCancel={() => setDeleteTarget(null)}
        onOk={() =>
          deleteFactory({ id: deleteTarget?._id, operatorId })
        }
        okText={t('delete')}
        cancelText={t('cancel')}
        okButtonProps={{ danger: true, loading: deleting }}
      >
        <p>{t('deleteConfirm')}</p>
        {deleteTarget?.number ? (
          <p>
            <strong>
              {deleteTarget.name
                ? `${deleteTarget.number} — ${deleteTarget.name}`
                : deleteTarget.number}
            </strong>
          </p>
        ) : null}
      </Modal>
    </div>
  );
}
